import { FC, useEffect, useRef, useState, useContext, useCallback } from "react";
import { setupChatIframe } from "@src/lib/chat/iframe";
import { authenticateChatSession } from "@src/lib/chat/auth";
import { WalterContext } from "@src/taskpane/providers/walter";
import { useAppReady } from "@src/taskpane/hooks/useAppReady";
import Progress from "../Progress";
import { captureErrorInfo } from "@src/taskpane/helpers/errorHandler";
import { makeStyles, Button } from "@fluentui/react-components";
import { tokenStorage } from "@src/lib/settings/tokenStorage";
import { revokeToken } from "@src/lib/auth/OAuthService";
import { SyncEngine } from "@src/lib/sync-engine/SyncEngine";
import { ChatDocumentEventHandler } from "@src/lib/chat/document-event-handler";
import { createWordRpcBridge, WordRpcBridge } from "@src/lib/chat/word-rpc-bridge";
import { createIframeMessageHandler } from "@shared/lib/iframe-messages.ts";
import { WALTER_HOST } from "@src/lib/env-vars";

const useStyles = makeStyles({
  container: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    backgroundColor: "#fff",
    zIndex: 1000,
  },
  iframeContainer: {
    width: "100%",
    height: "100%",
    overflow: "hidden",
  },
  errorContainer: {
    padding: "24px",
    textAlign: "center",
  },
  errorTitle: {
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "12px",
  },
  errorMessage: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "16px",
  },
});

interface ChatViewProps {
  onSwitchToAutomation?: () => void;
  automationModeEnabled?: boolean;
}

export const ChatView: FC<ChatViewProps> = ({ onSwitchToAutomation, automationModeEnabled }) => {
  const styles = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const initializedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthError, setIsAuthError] = useState(false);
  const { currentWorkspace, setIsLoggedIn } = useContext(WalterContext);
  const { setAppReady } = useAppReady();

  // Refs for sync engine, RPC bridge, and document event handler
  const wordSyncEngineRef = useRef<SyncEngine | null>(null);
  const rpcBridgeRef = useRef<WordRpcBridge | null>(null);
  const documentEventHandlerRef = useRef<ChatDocumentEventHandler | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const initializeChat = useCallback(
    async (container: HTMLDivElement, workspaceId: string) => {
      if (initializedRef.current) {
        return;
      }
      initializedRef.current = true;

      try {
        setLoading(true);
        setError(null);
        setIsAuthError(false);

        // Authenticate the chat session
        await authenticateChatSession();

        // Create and start the Word SyncEngine for document operations
        const wordSyncEngine = new SyncEngine();
        wordSyncEngine.start();
        wordSyncEngineRef.current = wordSyncEngine;

        // TODO: Conversation storage is disabled until per-document scoping is implemented
        // const savedConversationId = getStoredConversationId(workspaceId);

        // Set up the chat iframe
        const iframe = setupChatIframe(container, workspaceId, undefined);
        iframeRef.current = iframe;

        // Create RPC bridge (handles iframe load waiting internally)
        const rpcBridge = await createWordRpcBridge({
          iframe,
          workspaceId,
          syncEngine: wordSyncEngine,
          automationModeEnabled,
          onSwitchMode: (mode) => {
            if (mode === "automation") {
              onSwitchToAutomation?.();
            }
          },
        });
        rpcBridgeRef.current = rpcBridge;

        // Set up document event handler to push surface context on selection changes
        const docHandler = new ChatDocumentEventHandler(() => rpcBridge.pushSurfaceContext());
        await docHandler.setup();
        documentEventHandlerRef.current = docHandler;

        setLoading(false);
      } catch (err) {
        console.error("Failed to initialize chat:", err);
        captureErrorInfo(err as Error);

        const errorMessage = err instanceof Error ? err.message : "Failed to load chat. Please try again.";

        // Check if this is an authentication error
        const isAuth =
          errorMessage.includes("401") ||
          errorMessage.includes("Unauthorized") ||
          errorMessage.includes("scope") ||
          errorMessage.includes("log in") ||
          errorMessage.includes("log out");

        setIsAuthError(isAuth);
        setError(errorMessage);
        setLoading(false);
        initializedRef.current = false;

        // Clean up any partially initialized components
        documentEventHandlerRef.current?.cleanup();
        documentEventHandlerRef.current = null;
        rpcBridgeRef.current?.cleanup();
        rpcBridgeRef.current = null;
        wordSyncEngineRef.current?.stop();
        wordSyncEngineRef.current = null;
      }
    },
    [onSwitchToAutomation, automationModeEnabled],
  );

  const handleRetry = useCallback(() => {
    // Clean up before retrying
    documentEventHandlerRef.current?.cleanup();
    documentEventHandlerRef.current = null;
    rpcBridgeRef.current?.cleanup();
    rpcBridgeRef.current = null;
    wordSyncEngineRef.current?.stop();
    wordSyncEngineRef.current = null;
    iframeRef.current = null;

    initializedRef.current = false;
    setError(null);
    setIsAuthError(false);
    if (containerRef.current && currentWorkspace?.id) {
      void initializeChat(containerRef.current, currentWorkspace.id.toString());
    }
  }, [currentWorkspace?.id, initializeChat]);

  const handleLogout = useCallback(async () => {
    const token = await tokenStorage.get("walter");
    if (token) {
      try {
        await revokeToken(token.accessToken);
        tokenStorage.clear();
        setIsLoggedIn(false);
        setAppReady(false);
      } catch (e) {
        captureErrorInfo(e as Error);
      }
    }
    window.location.reload();
  }, [setIsLoggedIn, setAppReady]);

  // Handle sign out messages from the chat iframe
  useEffect(() => {
    const handler = createIframeMessageHandler(WALTER_HOST, {
      onSignOut: handleLogout,
    });
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [handleLogout]);

  // Use callback ref to initialize as soon as the container is mounted
  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (node && currentWorkspace?.id && !initializedRef.current) {
        void initializeChat(node, currentWorkspace.id.toString());
      }
    },
    [currentWorkspace?.id, initializeChat],
  );

  // Handle workspace change or show error if no workspace
  useEffect(() => {
    if (!currentWorkspace?.id) {
      // Give it a moment to load, then show error
      const timeout = setTimeout(() => {
        if (!currentWorkspace?.id && !initializedRef.current) {
          setLoading(false);
          setError("No workspace selected. Please select a workspace to use chat.");
        }
      }, 3000);
      return () => clearTimeout(timeout);
    }

    if (containerRef.current && !initializedRef.current) {
      void initializeChat(containerRef.current, currentWorkspace.id.toString());
    }
  }, [currentWorkspace?.id, initializeChat]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up document event handler (must be before RPC bridge)
      if (documentEventHandlerRef.current) {
        documentEventHandlerRef.current.cleanup();
        documentEventHandlerRef.current = null;
      }

      // Clean up RPC bridge
      if (rpcBridgeRef.current) {
        rpcBridgeRef.current.cleanup();
        rpcBridgeRef.current = null;
      }

      // Clean up Word SyncEngine
      if (wordSyncEngineRef.current) {
        wordSyncEngineRef.current.stop();
        wordSyncEngineRef.current = null;
      }

      iframeRef.current = null;
      initializedRef.current = false;
    };
  }, []);

  return (
    <div className={styles.container}>
      {loading && <Progress message="Loading chat..." />}
      {error && (
        <div className={styles.errorContainer}>
          <div className={styles.errorTitle}>Unable to load chat</div>
          <div className={styles.errorMessage}>{error}</div>
          <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
            {isAuthError && (
              <Button appearance="primary" onClick={handleLogout}>
                Log out and log back in
              </Button>
            )}
            <Button appearance={isAuthError ? "secondary" : "primary"} onClick={handleRetry}>
              Retry
            </Button>
          </div>
        </div>
      )}
      <div
        ref={setContainerRef}
        className={styles.iframeContainer}
        style={{ display: loading || error ? "none" : "block" }}
      />
    </div>
  );
};
